import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, isSameDay, parse, isValid } from 'date-fns';
import {
  Draggable,
  Dropcontainer,
  MbscCalendarEvent,
  MbscItemDragEvent,
  snackbar,
} from '@akoom-tech/mobiscroll-react';
//
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
//
import { Iconify, Label } from '@/components';
import { ToolbarWithSearchAndStatus } from '@/components/toolbar';
// API
import {
  getAllFilteredBookingSessions,
  getFilteredBookingSessions,
  updateBookingSession,
} from '@/features/bookings-management/api';
import { getSettingsData } from '@/features/app/api';
// hooks + utils
import useTable, { getComparator } from '@/hooks/useTable';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import {
  applyFilterJobs,
  buildUnassignedSessionsMonthGroups,
  getNextPrevDay,
  parseBookingSessionData,
  parseValidDateValue,
} from '../../utils';
import {
  buildCoverageSummary,
  buildMatchRequestPayload,
  buildMissingMatchReason,
  coveragePillMeta,
  filterAvailableMatches,
  getMatchLabel,
} from '../../utils/coverage';
import {
  ProviderMatchBatchRequest,
  ProviderMatchBatchResponseItem,
  getProviderMatchesBatch,
} from '@/features/users/providers/api/matches';
import { CoverageSummary } from '../../types/coverage';
// type
import { TransformedBooking } from '../../types';
import CleaningJobDetailsDialog from '../cleaning-job-dialog/CleaningJobDetailsDialog';
import useToggle from '@/hooks/useToggle';

type UnassignedEventsContainerProps = {
  selectedDate: string | Date | undefined;
  onNavigateToDate?: (date: Date) => void;
  onSelectSession?: (session: TransformedBooking) => void;
  selectedSessionId?: number | null;
};

type UnassignedMatchMeta = {
  summary: CoverageSummary | null;
  matchesCount: number;
  reason?: string;
  isPending: boolean;
};

export default function UnassignedEventsContainer({
  selectedDate,
  onNavigateToDate,
  onSelectSession,
  selectedSessionId,
}: UnassignedEventsContainerProps) {
  const COMPONENT_MIN_HEIGHT = 400; // the container needs a minimum height
  const [filterServiceTypeId, setFilterServiceTypeId] = useState<number>(0);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [showOtherUnassigned, setShowOtherUnassigned] = useState(false);

  const [dropCont, setDropCont] = useState<HTMLDivElement>();
  const { errorMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();

  const {
    toggle: isJobDialogOpen,
    onClose: closeJobDialog,
    onOpen: openJobDialog,
  } = useToggle();

  const handleEditSession = (id: number) => {
    setCurrentSessionId(id);
    openJobDialog();
  };

  // QUERIES
  const selectedDateValue = useMemo(
    () => parseValidDateValue(selectedDate) ?? new Date(),
    [selectedDate]
  );
  const nextDay = getNextPrevDay(selectedDateValue, 'next');
  const prevDay = getNextPrevDay(selectedDateValue, 'prev');
  const {
    data: unassignedBookingSessions,
    isLoading: isBookingSessionsDataLoading,
  } = useQuery({
    queryKey: ['unassignedJobs', selectedDate],
    queryFn: () =>
      getFilteredBookingSessions(
        `unassigned`,
        `?startDate=${prevDay}&endDate=${nextDay}`
      ),
    onError: (error) => errorMsg(error, 'Error fetching cleaning jobs'),
    select: (data) =>
      parseBookingSessionData(data || []).filter((session) => {
        const sessionStart = parseValidDateValue(session.start);

        return sessionStart
          ? isSameDay(selectedDateValue, sessionStart)
          : false;
      }) || [],
  });

  const {
    data: allUnassignedBookingSessions,
    isLoading: isAllUnassignedLoading,
  } = useQuery({
    queryKey: ['unassignedJobs', 'all'],
    queryFn: async () => {
      const { data } = await getAllFilteredBookingSessions({
        sessionFilter: 'unassigned',
      });

      return data;
    },
    onError: (error) =>
      errorMsg(error, 'Error fetching all unassigned booking sessions'),
    select: (data) => parseBookingSessionData(data || []),
  });

  const { data: serviceTypes, isLoading: isSettingsDataLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    onError: (error) => errorMsg(error, 'Error fetching settings'),
    select: (data) => [{ id: 0, name: 'All' }, ...data.services],
  });

  // =================================
  //     REACT QUERY / UPDATE DATA
  // =================================

  // more info on useMutation --> https://www.youtube.com/watch?v=DocXo3gqGdI&t=4431s
  const unassignBookingSessionMutation = useMutation({
    // mutate function will make the API call
    mutationFn: (updatedSession: MbscCalendarEvent) =>
      updateBookingSession(Number(updatedSession.id), {
        booking_cleaners: [],
      }),
    // onMutate fires before the mutationFn and optimistically updates the cache
    onMutate: (updatedSessionFromScheduler) => {
      const oldSchedulerData = queryClient.getQueryData(['scheduler']);

      if (queryClient.getQueryData(['scheduler'])) {
        // TODO: TS any
        queryClient.setQueryData(['scheduler'], (old: any) =>
          old.filter((b: any) => b.id === updatedSessionFromScheduler.id)
        );
      }

      // this will be used in the onError callback
      return () => queryClient.setQueryData(['scheduler'], oldSchedulerData);
    },

    onError: (error, _v, rollback) => {
      // this fn is returned from onMutate callback
      rollback && rollback();
      errorMsg(error, `Something went wrong while updating booking`);
    },

    onSettled: (_data, error, mutationData) => {
      // if mutation was successful, we show a snackbar with the option to undo the mutation
      if (!!!error) {
        snackbar({
          message: `#${mutationData.id} successfully unassigned`,
          color: 'success',
          duration: 6000,
        });
      }
      queryClient.invalidateQueries(['scheduler']);
      queryClient.invalidateQueries(['unassignedJobs', selectedDate]);
      queryClient.invalidateQueries(['unassignedJobs', 'all']);
    },
  });

  const setDropElm = useCallback((element: HTMLDivElement) => {
    setDropCont(element);
  }, []);
  const handleItemDrop = useCallback(
    (args: MbscItemDragEvent) => {
      unassignBookingSessionMutation.mutate(args.data);
    },
    [unassignBookingSessionMutation]
  );

  const [showSearch, setShowSearch] = useState(false);
  // ==============================
  //          QUICK FILTER
  // ==============================

  const [searchFieldValue, setSearchFieldValue] = useState('');

  const handleSearchFieldValue = (searchFieldValue: string) => {
    setSearchFieldValue(searchFieldValue);
  };

  const { order, orderBy } = useTable({ defaultDense: true });
  const dataFiltered = applyFilterJobs({
    tableData: unassignedBookingSessions || [],
    comparator: getComparator(order, orderBy),
    filterServiceTypeId,
  });

  const isNotFound = !!!dataFiltered.length;

  const matchEntries = useMemo(
    () =>
      (dataFiltered || []).map((session) => {
        const request = buildMatchRequestPayload(session, { limit: 5 });
        if (request) {
          return { sessionId: session.id, request } as const;
        }
        return {
          sessionId: session.id,
          reason: buildMissingMatchReason(session),
        } as const;
      }),
    [dataFiltered]
  );

  const matchRequests = useMemo(
    () =>
      matchEntries
        .map((entry) => entry.request)
        .filter((request): request is ProviderMatchBatchRequest =>
          Boolean(request)
        ),
    [matchEntries]
  );

  const matchRequestsKey = useMemo(() => {
    if (!matchRequests.length) return 'none';
    return matchRequests
      .map((request) =>
        [
          request.reference ?? '',
          request.outward_code,
          request.service_type,
          request.date ?? '',
          request.address_id ?? '',
          request.client_user_id ?? '',
          request.limit ?? '',
        ].join('-')
      )
      .join('|');
  }, [matchRequests]);

  const selectedDateKey = useMemo(
    () => selectedDateValue.toISOString(),
    [selectedDateValue]
  );

  const {
    data: unassignedMatchResults = [],
    isFetching: isUnassignedMatchesFetching,
  } = useQuery({
    queryKey: ['unassignedMatches', selectedDateKey, matchRequestsKey],
    queryFn: () => getProviderMatchesBatch(matchRequests),
    enabled: matchRequests.length > 0,
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });

  const matchesBySessionId = useMemo(() => {
    const map = new Map<number, ProviderMatchBatchResponseItem>();
    unassignedMatchResults.forEach((entry) => {
      const ref = entry.request?.reference;
      if (!ref) return;
      const sessionId = Number(ref);
      if (Number.isNaN(sessionId)) return;
      const availableMatches = filterAvailableMatches(entry.matches);
      const orderedMatches = [...availableMatches].sort((a, b) => {
        const aCount = a.assigned_bookings_at_address ?? 0;
        const bCount = b.assigned_bookings_at_address ?? 0;
        if (aCount !== bCount) {
          return bCount - aCount;
        }
        return a.provider_id - b.provider_id;
      });
      map.set(sessionId, {
        ...entry,
        matches: orderedMatches,
        reason:
          availableMatches.length === 0
            ? entry.reason ||
              'All matching cleaners are busy for this timeslot.'
            : entry.reason,
      });
    });
    return map;
  }, [unassignedMatchResults]);

  const unassignedMatchMeta = useMemo(() => {
    const map = new Map<number, UnassignedMatchMeta>();

    matchEntries.forEach((entry) => {
      const matchEntry = matchesBySessionId.get(entry.sessionId);
      const summary = buildCoverageSummary(matchEntry);
      const matchesCount = matchEntry?.matches?.length ?? 0;
      const reason = matchEntry?.reason ?? entry.reason;
      const isPending = Boolean(
        entry.request &&
          !matchEntry &&
          matchRequests.length > 0 &&
          isUnassignedMatchesFetching
      );

      map.set(entry.sessionId, {
        summary,
        matchesCount,
        reason,
        isPending,
      });
    });

    return map;
  }, [
    isUnassignedMatchesFetching,
    matchEntries,
    matchRequests.length,
    matchesBySessionId,
  ]);

  const handleNavigateToUnassignedDate = useCallback(
    (dateKey: string) => {
      if (!onNavigateToDate) return;
      const parsedDate = parse(dateKey, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        onNavigateToDate(parsedDate);
      }
    },
    [onNavigateToDate]
  );

  const otherUnassignedSessions = useMemo(() => {
    if (!allUnassignedBookingSessions?.length) {
      return [] as TransformedBooking[];
    }

    const currentDayIds = new Set(
      (unassignedBookingSessions || []).map((session) => session.id)
    );

    return allUnassignedBookingSessions.filter(
      (session) => !currentDayIds.has(session.id)
    );
  }, [allUnassignedBookingSessions, unassignedBookingSessions]);

  const groupedUnassignedByMonth = useMemo(
    () => buildUnassignedSessionsMonthGroups(otherUnassignedSessions),
    [otherUnassignedSessions]
  );

  return (
    <>
      <Card
        ref={setDropElm}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: COMPONENT_MIN_HEIGHT,
          p: 1,
          backgroundColor: 'paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            pl: 0.5,
          }}
        >
          <Typography fontWeight={600}>Unassigned sessions</Typography>

          <IconButton onClick={() => setShowSearch(!showSearch)}>
            <Iconify icon="eva:search-outline" />
          </IconButton>
        </Stack>
        {showSearch && (
          <Box sx={{ pb: 0.5, px: 1.5 }}>
            <ToolbarWithSearchAndStatus
              filterText={searchFieldValue}
              onFilterText={handleSearchFieldValue}
              searchfieldVariant="standard"
              labels={{ search: 'Search in jobs...' }}
            />
          </Box>
        )}
        {isSettingsDataLoading ? (
          <Skeleton />
        ) : (
          <TextField
            select
            size="small"
            value={filterServiceTypeId}
            onChange={(e) => setFilterServiceTypeId(Number(e.target.value))}
          >
            {(serviceTypes || []).map((option) => (
              <MenuItem value={option.id} key={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        {isBookingSessionsDataLoading || !dataFiltered ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', minHeight: COMPONENT_MIN_HEIGHT }}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <Stack
            spacing={1}
            ref={setDropElm}
            sx={{ height: '100%', minHeight: COMPONENT_MIN_HEIGHT }}
          >
            <Dropcontainer
              onItemDrop={handleItemDrop}
              element={dropCont}
              style={{ height: '100%', width: '100%' }}
            >
              {dataFiltered.map((task) => {
                const matchMeta = unassignedMatchMeta.get(task.id);
                return (
                  <UnassignedJob
                    key={task.id}
                    event={task}
                    handleEditSession={handleEditSession}
                    onSelectSession={onSelectSession}
                    isSelected={selectedSessionId === task.id}
                    matchSummary={matchMeta?.summary}
                    matchesCount={matchMeta?.matchesCount}
                    matchReason={matchMeta?.reason}
                    matchesLoading={matchMeta?.isPending}
                  />
                );
              })}
              {isNotFound && (
                <Stack
                  spacing={1}
                  justifyContent="center"
                  alignItems={'center'}
                  sx={{ textAlign: 'center', pt: 1.5 }}
                >
                  {filterServiceTypeId !== 0 ? (
                    <Typography>
                      No unassigned jobs available for the selected filter
                    </Typography>
                  ) : (
                    <>
                      <Typography>
                        No unassigned jobs available for the selected date:
                      </Typography>
                      <Typography fontWeight={600}>
                        {format(selectedDateValue, 'EEE, dd-MM-yyyy')}
                      </Typography>
                    </>
                  )}
                </Stack>
              )}
            </Dropcontainer>
          </Stack>
        )}

        {isAllUnassignedLoading ? (
          <Skeleton variant="rectangular" height={72} />
        ) : groupedUnassignedByMonth.length > 0 ? (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography fontWeight={600}>
                  Other unassigned sessions
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowOtherUnassigned((prev) => !prev)}
                >
                  {showOtherUnassigned ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Stack>

              <Collapse in={showOtherUnassigned} unmountOnExit>
                <Stack spacing={1} sx={{ width: '100%' }}>
                  {groupedUnassignedByMonth.map((group) => (
                    <Accordion
                      key={group.monthLabel}
                      disableGutters
                      elevation={0}
                      sx={{
                        '&:before': { display: 'none' },
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="small" />}
                        sx={{
                          px: 1.5,
                          py: 1,
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            gap: 1,
                          },
                        }}
                      >
                        <Typography fontWeight={600}>
                          {group.monthLabel}
                        </Typography>
                        <Chip
                          color="info"
                          size="small"
                          label={`${group.total} session${
                            group.total === 1 ? '' : 's'
                          }`}
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                        <Stack spacing={1}>
                          {group.days.map((day) => (
                            <ButtonBase
                              key={day.dateKey}
                              onClick={() =>
                                handleNavigateToUnassignedDate(day.dateKey)
                              }
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                borderRadius: 1,
                                px: 1,
                                py: 0.75,
                                textAlign: 'left',
                                '&:hover': {
                                  bgcolor: (theme) =>
                                    theme.palette.action.hover,
                                },
                              }}
                            >
                              <Typography>{day.dateLabel}</Typography>
                              <Chip
                                size="small"
                                label={`${day.total} session${
                                  day.total === 1 ? '' : 's'
                                }`}
                              />
                            </ButtonBase>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          </>
        ) : null}
      </Card>

      <CleaningJobDetailsDialog
        isJobDialogOpen={isJobDialogOpen}
        closeJobDialog={closeJobDialog}
        currentSessionId={currentSessionId}
      />
    </>
  );
}

function UnassignedJob({
  event,
  handleEditSession,
  onSelectSession,
  isSelected = false,
  matchSummary,
  matchesCount,
  matchReason,
  matchesLoading,
}: {
  event: TransformedBooking;
  handleEditSession: (id: number) => void;
  onSelectSession?: (session: TransformedBooking) => void;
  isSelected?: boolean;
  matchSummary?: CoverageSummary | null;
  matchesCount?: number;
  matchReason?: string;
  matchesLoading?: boolean;
}) {
  const [draggable, setDraggable] = useState<HTMLDivElement>();

  const setDragElm = useCallback((elm: HTMLDivElement) => {
    setDraggable(elm);
  }, []);

  const eventLength = event.totalHours || 0;
  const durationLabel = `${eventLength} hr${eventLength === 1 ? '' : 's'}`;

  const startDate = new Date(event.start);
  const endDate = new Date(event.end);
  const hasValidStart = isValid(startDate);
  const hasValidEnd = isValid(endDate);
  const timeRangeLabel =
    hasValidStart && hasValidEnd
      ? `${format(startDate, 'HH:mm')}-${format(endDate, 'HH:mm')}`
      : null;
  const sessionTimingLabel = timeRangeLabel
    ? `${timeRangeLabel} (${durationLabel})`
    : durationLabel;

  const handleCardClick = useCallback(() => {
    onSelectSession?.(event);
  }, [event, onSelectSession]);

  const resolvedMatchesCount =
    typeof matchesCount === 'number' ? matchesCount : 0;
  const hasSummary = Boolean(matchSummary);
  const matchesChipColor: 'default' | 'success' | 'warning' | 'error' =
    hasSummary && matchSummary
      ? coveragePillMeta[matchSummary.coverage].color
      : 'default';
  const matchesChipLabel = hasSummary
    ? getMatchLabel(resolvedMatchesCount)
    : matchReason
    ? 'Needs info'
    : getMatchLabel(resolvedMatchesCount);
  const matchesChipTooltip = hasSummary ? matchSummary?.message : matchReason;
  const showAirbnbBadge = event.property?.icon === 'logos:airbnb-icon';

  return (
    <Card
      sx={{
        p: 1,
        cursor: 'pointer',
        border: (theme) =>
          `1px solid ${
            isSelected ? theme.palette.primary.main : theme.palette.divider
          }`,
        backgroundColor: isSelected ? 'primary.lighter' : 'background.paper',
        ':hover': { backgroundColor: 'warning.lighter' },
      }}
      onClick={handleCardClick}
      onDoubleClick={() => handleEditSession(event.id)}
    >
      <div ref={setDragElm}>
        <Stack
          direction="column"
          alignItems="flex-end"
          spacing={0.5}
          sx={{
            position: 'absolute',
            right: 1,
            top: 1,
            p: 1,
          }}
        >
          {matchesLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Tooltip
              title={matchesChipTooltip ?? ''}
              disableHoverListener={!matchesChipTooltip}
            >
              <Chip
                label={matchesChipLabel}
                size="small"
                color={matchesChipColor}
                variant="outlined"
              />
            </Tooltip>
          )}
          {/* SERVICE + RECURRENCE LABEL, ie PO - personal one-off */}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}
            sx={{ flexWrap: 'wrap', maxWidth: 120 }}
          >
            {event.isTestAccount && (
              <Tooltip title="Test account" arrow>
                <Label
                  color="warning"
                  variant="filled"
                  sx={{ p: 0, height: 24, minWidth: 42, flexShrink: 0 }}
                >
                  TEST
                </Label>
              </Tooltip>
            )}
            {showAirbnbBadge && (
              <Tooltip
                title={event.property.iconTitle ?? event.property.type}
                arrow
              >
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    width: 24,
                    height: 24,
                    borderRadius: 0.75,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
                    flexShrink: 0,
                  }}
                >
                  <Iconify icon={event.property.icon} width={17} />
                </Box>
              </Tooltip>
            )}

            <Tooltip
              title={event.serviceLabelDescription || 'Unknown service type'}
              arrow
            >
              <Label
                color="info"
                variant="filled"
                sx={{ p: 0, height: 24, minWidth: 32, flexShrink: 0 }}
              >
                {event.serviceLabel}
              </Label>
            </Tooltip>
          </Stack>
        </Stack>
        <Typography fontWeight={600}>{event.title}</Typography>
        <Typography fontSize={12}>{sessionTimingLabel}</Typography>
        <Typography fontSize={12}>{event.address.postcode}</Typography>
        <Draggable dragData={event} element={draggable} />
      </div>
    </Card>
  );
}
